import { Controller } from 'stimulus';
import gsap from 'gsap';
import toArray from '../helpers/toArray';

class FilterBarController extends Controller {
  connect() {
    this.page = 1;

    if (typeof URLSearchParams === 'function') {
      const urlParameters = new URLSearchParams(window.location.search);

      if (urlParameters.get('page')) {
        this.page = parseInt(urlParameters.get('page'), 10);
      }
    }

    this.filters = toArray(this.element.querySelectorAll('.select, .form-input'));

    this.boundOnFilterChange = this.onFilterChange.bind(this);

    this.filters.forEach((filter) => {
      filter.addEventListener('change', this.boundOnFilterChange);
    });
  }

  onFilterChange() {
    this.page = 1;
    this.runFilter();
  }

  onSearchChange() {
    this.runFilter();
  }

  onPaginate() {
    this.page += 1;

    this.paginateTarget.setAttribute('disabled', 'disabled');

    this.runFilter(true)
      .finally(() => {
        this.paginateTarget.removeAttribute('disabled');
      });
  }

  /**
   * @param {Boolean} append
   * @returns {Promise<Object>}
   */
  runFilter(append = false) {
    const formData = new FormData(this.filterFormTarget);

    formData.append('page', this.page);

    this.updateFiltersCount(formData);

    const parameters = new URLSearchParams(formData);

    if (typeof window.history.replaceState === 'function') {
      window.history.replaceState(null, null, parameters ? `?${parameters}` : window.location.pathname);
    }

    return this.callFilter(parameters)
      .then((res) => {
        this.outputResults(res, append);

        return res;
      });
  }

  /**
   * @param {Object} res
   * @param {boolean} append
   * @returns {Object}
   */
  outputResults(res) {
    this.resultsTarget.innerHTML = '';

    const parser = new DOMParser();
    const document = parser.parseFromString(res, 'text/html');

    const results = document.querySelector('[data-target="filter-bar.results"]');
    const pagination = document.querySelector('[data-target="filter-bar.pagination"]');

    const newElementsWrapper = document.createElement('div');
    newElementsWrapper.innerHTML = results.innerHTML;

    const newElements = toArray(newElementsWrapper.children);

    newElements.forEach((child) => {
      child.style.opacity = 0;
      this.resultsTarget.appendChild(child);
    });

    gsap.fromTo(newElements, {
      opacity: 0,
      y: 20,
    }, {
      opacity: 1,
      y: 0,
      stagger: 0.2,
      ease: 'power2.out',
    });

    this.paginationTarget.innerHTML = pagination.innerHTML;

    return res;
  }

  /**
   * @param {FormData} formData
   */
  updateFiltersCount(formData) {
    const formDataArray = FilterBarController.formDataArray(formData)
      .filter((item) => item[0] !== 'page' && item[0] !== 'search');

    const filtersCount = formDataArray.length;

    this.filterToggleCountTarget.querySelector('span').textContent = filtersCount;
    this.filterToggleCountTarget.style.opacity = filtersCount > 0 ? 1 : 0;
  }

  onToggleFilters() {
    const isOpen = this.filterTarget.getAttribute('aria-expanded') === 'true';

    this.visuallyToggleFilters(!isOpen);

    this.filterTarget.setAttribute('aria-expanded', !isOpen);

    if (!isOpen) {
      this.filterTarget.focus();
    } else {
      this.filterToggleTarget.focus();
    }
  }

  /**
   * @param {boolean} show
   */
  visuallyToggleFilters(show) {
    let toOpacity = 0;

    if (show) {
      // Start at an opacity of 0...
      this.filterTarget.style.opacity = 0;
      this.filterTarget.style.display = 'block';

      toOpacity = 1;
    }

    gsap.to(this.filterTarget, {
      opacity: toOpacity,
      ease: 'power2.out',
      duration: 0.5,
      onComplete: () => {
        if (!show) {
          this.filterTarget.style.display = 'none';
        }
      },
    });
  }

  /**
   * @param {String} parameters
   */
  callFilter(parameters) {
    return new Promise((resolve) => {
      fetch(`${this.data.get('api')}?${parameters}`)
        .then((res) => res.text())
        .then(resolve);
    });
  }

  /**
   * @param {FormData} formData
   */
  static formDataArray(formData) {
    if (typeof formData.entries !== 'function') {
      return [];
    }

    const entries = [...formData.entries()];

    return entries.filter((entry) => entry[1]);
  }
}

FilterBarController.targets = [
  'filter',
  'filterToggle',
  'filterForm',
  'filterToggleCount',
  'results',
  'pagination',
];

export default FilterBarController;
